import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

function formatSigned(value, positivePrefix = "+") {
  const n = Number(value || 0);
  if (n > 0) return `${positivePrefix}${n}`;
  if (n < 0) return `${n}`;
  return "0";
}

function StatCard({ title, value, subtitle, badge, accentClass = "border-slate-400", valueClass = "text-[var(--ink,#1c1c1a)]" }) {
  return (
    <div className="relative rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
      {badge ? (
        <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded-full bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]">
          {badge}
        </span>
      ) : null}
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">{title}</p>
      <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-tight ${valueClass}`}>{value}</p>
      <p className="mt-1 text-[12px] leading-snug text-[var(--muted,#6b6a66)]">{subtitle}</p>
    </div>
  );
}

function rowsToCsv(rows, totalRow) {
  const headers = [
    "Site Classification",
    "Baseline Target",
    "Sites Opened",
    "Sites Closed",
    "Net Delta",
    "Current Active Sites",
  ];

  const lines = [headers.join(",")];

  rows.forEach((r) => {
    const row = [
      `"${r.siteClassification}"`,
      Number(r.baselineTarget || 0),
      Number(r.sitesOpenedYtd || 0),
      Number(r.sitesClosedYtd || 0),
      Number(r.netSiteDelta || 0),
      Number(r.currentActiveSites || 0),
    ];
    lines.push(row.join(","));
  });

  // ✅ include total row
  if (totalRow) {
    const total = [
      `"${totalRow.siteClassification}"`,
      Number(totalRow.baselineTarget || 0),
      Number(totalRow.sitesOpenedYtd || 0),
      Number(totalRow.sitesClosedYtd || 0),
      Number(totalRow.netSiteDelta || 0),
      Number(totalRow.currentActiveSites || 0),
    ];
    lines.push(total.join(","));
  }

  return lines.join("\r\n");
}

const Report20SiteFootprintChangesView = ({ data }) => {
  const statCards = data?.statCards || {};
  const allRows = data?.table?.rows || [];
  const detailRows = useMemo(
    () => allRows.filter((r) => String(r.siteClassification).toLowerCase() !== "global total"),
    [allRows]
  );
  const totalRow = useMemo(
    () => allRows.find((r) => String(r.siteClassification).toLowerCase() === "global total") || null,
    [allRows]
  );

  const maxActive = useMemo(() => {
    const maxFromRows = detailRows.reduce((m, r) => Math.max(m, Number(r.currentActiveSites || 0)), 0);
    return Math.max(maxFromRows, 1);
  }, [detailRows]);

  const activityChartRows = useMemo(
    () =>
      detailRows.map((row) => ({
        siteClassification: row.siteClassification,
        opened: Number(row.sitesOpenedYtd || 0),
        // Keep closed below baseline for visual weighting
        closed: Number(row.sitesClosedYtd || 0) * -1,
      })),
    [detailRows]
  );

  const chartDomain = useMemo(() => {
    if (!activityChartRows.length) return [-1, 1];
    let minV = 0;
    let maxV = 0;
    for (const row of activityChartRows) {
      minV = Math.min(minV, Number(row.closed || 0));
      maxV = Math.max(maxV, Number(row.opened || 0));
    }
    const pad = Math.max(1, Math.ceil(Math.max(Math.abs(minV), Math.abs(maxV)) * 0.2));
    return [minV - pad, maxV + pad];
  }, [activityChartRows]);

  const renderActivityTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const opened = Math.abs(Number(payload.find((p) => p.dataKey === "opened")?.value || 0));
    const closed = Math.abs(Number(payload.find((p) => p.dataKey === "closed")?.value || 0));
    return (
      <div className="bg-white px-3 py-2 border border-gray-200 rounded-lg shadow-md text-xs">
        <p className="font-semibold text-slate-900 mb-1">{label}</p>
        <p className="text-emerald-700">Sites Opened: +{opened}</p>
        <p className="text-rose-700">Sites Closed: -{closed}</p>
      </div>
    );
  };

  const exportCsv = () => {
    const csv = rowsToCsv(detailRows, totalRow);
  
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
  
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
  
    a.href = url;
    a.download = "site-footprint-report.csv";
    a.click();
  
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <StatCard
          title="Baseline Target"
          value={Number(statCards.baselineTarget || 0)}
          subtitle="Approved active locations at FY start"
          badge="FY"
          accentClass="border-t-slate-600"
        />
        {/* <StatCard
          title="Sites Opened YTD"
          value={formatSigned(statCards.sitesOpenedYtd, "+")}
          subtitle="Distinct locations opened this fiscal year"
          badge="YTD"
          accentClass="border-t-emerald-500"
          valueClass="text-emerald-600"
        />
        <StatCard
          title="Sites Closed YTD"
          value={formatSigned(-Math.abs(Number(statCards.sitesClosedYtd || 0)), "+")}
          subtitle="Distinct locations closed this fiscal year"
          badge="YTD"
          accentClass="border-t-rose-500"
          valueClass="text-rose-600"
        /> */}

<StatCard
  title="Active Sites"
  value={statCards.sitesOpenedYtd}
  subtitle="Currently active locations"
  badge="LIVE"
accentClass="border-t-slate-600"
/>

<StatCard
  title="Closed Sites"
  value={statCards.sitesClosedYtd}
  subtitle="Currently closed locations"
  badge="LIVE"
accentClass="border-t-slate-600"
/>
        <StatCard
          title="Net Site Delta"
          value={formatSigned(statCards.netSiteDelta, "+")}
          subtitle="Opened minus closed"
          badge={Number(statCards.netSiteDelta || 0) > 0 ? "GROWTH" : Number(statCards.netSiteDelta || 0) < 0 ? "REDUCTION" : "NEUTRAL"}
          accentClass="border-t-slate-600"
          valueClass={
            Number(statCards.netSiteDelta || 0) > 0
              ? "text-slate-600"
              : Number(statCards.netSiteDelta || 0) < 0
              ? "text-slate-600"
              : "text-slate-600"
          }
        />
        <StatCard
          title="Current Active Sites"
          value={Number(statCards.currentActiveSites || 0)}
          subtitle="Current active operational locations"
          badge="LIVE"
accentClass="border-t-slate-600"
        />
      </div>

      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
        <div className="px-5 pt-4 pb-3">
          <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">Site Activity (Openings vs. Closings) by Tier</h3>
        </div>
        <div className="px-3 py-4 h-[320px]">
          {activityChartRows.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">No site activity data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityChartRows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="siteClassification" tick={{ fontSize: 11 }} interval={0} />
                <YAxis domain={chartDomain} tick={{ fontSize: 11 }} />
                <Tooltip content={renderActivityTooltip} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(v) => (
                    <span className={v === "opened" ? "text-emerald-700" : "text-rose-700"}>
                      {v === "opened" ? "Sites Opened" : "Sites Closed"}
                    </span>
                  )}
                />
                <Bar dataKey="opened" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="closed" fill="#b91c1c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
      <div className="flex justify-between items-center px-5 pt-4 pb-3">
  
  <div>
    <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">
      Footprint Tracking by Site Classification
    </h3>
  </div>

  <button
    type="button"
    onClick={exportCsv}
    disabled={!detailRows.length}
    className="inline-flex h-[38px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:pointer-events-none"
  >
    Export
  </button>

</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--sunken2,#fbfaf8)] text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Site Classification</th>
                <th className="text-left px-4 py-3 font-semibold">Baseline Target</th>
                <th className="text-left px-4 py-3 font-semibold">Sites Opened</th>
                <th className="text-left px-4 py-3 font-semibold">Sites Closed</th>
                <th className="text-left px-4 py-3 font-semibold">Net Delta</th>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap w-[1%]">Current Active Sites</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((row) => {
                const active = Number(row.currentActiveSites || 0);
                const width = Math.max(4, Math.round((active / maxActive) * 100));
                const net = Number(row.netSiteDelta || 0);
                return (
                  <tr key={row.siteClassification} className="border-t border-[var(--line,#e4e2dc)]">
                    <td className="px-4 py-3 font-semibold text-slate-800">{row.siteClassification}</td>
                    <td className="px-4 py-3 text-slate-700">{Number(row.baselineTarget || 0)}</td>
                    <td className="px-4 py-3 text-emerald-700 font-medium">{formatSigned(row.sitesOpenedYtd, "+")}</td>
                    <td className="px-4 py-3 text-rose-700 font-medium">
                      {formatSigned(-Math.abs(Number(row.sitesClosedYtd || 0)), "+")}
                    </td>
                    <td className={`px-4 py-3 font-semibold ${net > 0 ? "text-amber-700" : net < 0 ? "text-rose-700" : "text-slate-700"}`}>
                      {formatSigned(net, "+")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 min-w-[1.75rem]">{active}</span>
                        {/* <div className="h-1.5 w-28 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${width}%` }} />
                        </div> */}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {totalRow && (
                <tr className="border-t-2 border-[var(--line,#e4e2dc)] bg-[var(--sunken2,#fbfaf8)]">
                  <td className="px-4 py-3 font-bold text-slate-900">{totalRow.siteClassification}</td>
                  <td className="px-4 py-3 font-bold text-slate-900">{Number(totalRow.baselineTarget || 0)}</td>
                  <td className="px-4 py-3 font-bold text-emerald-700">{formatSigned(totalRow.sitesOpenedYtd, "+")}</td>
                  <td className="px-4 py-3 font-bold text-rose-700">
                    {formatSigned(-Math.abs(Number(totalRow.sitesClosedYtd || 0)), "+")}
                  </td>
                  <td
                    className={`px-4 py-3 font-bold ${
                      Number(totalRow.netSiteDelta || 0) > 0
                        ? "text-amber-700"
                        : Number(totalRow.netSiteDelta || 0) < 0
                        ? "text-rose-700"
                        : "text-slate-700"
                    }`}
                  >
                    {formatSigned(totalRow.netSiteDelta, "+")}
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-900">{Number(totalRow.currentActiveSites || 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Report20SiteFootprintChangesView;

